import React, { useState, useEffect, useRef } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHeadset, FaSearch, FaPlus, FaEye, FaEdit, FaTrash, FaQuestionCircle } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import apiClient from '../../../shared/utils/apiConfig';

const AdminSupport = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);

    useEffect(() => {
        setTitle('إدارة الدعم');
    }, [setTitle]);

    useEffect(() => {
        fetchQuestions();
    }, [currentPage, searchTerm]);

    // استخدام useRef لتجنب إعادة تحميل الصفحة عند التنقل
    const isNavigating = useRef(false);

    useEffect(() => {
        return () => {
            isNavigating.current = true;
        };
    }, []);

    const fetchQuestions = async () => {
        // منع إعادة التحميل عند التنقل
        if (isNavigating.current) {
            return;
        }

        try {
            setLoading(true);

            // بناء الـ URL مع الفلاتر
            let url = `/admin/support-questions`;
            const params = new URLSearchParams();
            
            // إضافة معامل البحث
            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }
            
            // إضافة معامل الصفحة
            params.append('page', currentPage);
            
            // إضافة المعاملات للـ URL
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            
            const response = await apiClient.get(url);


            if (response.data.status === 200 || response.data.status === 'success' || response.data.statusCode === 200) {
                setQuestions(response.data.data || []);
                setPagination(response.data.pagination || null);
    
            } else {
                throw new Error(response.data.message || 'فشل في جلب أسئلة الدعم');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب أسئلة الدعم');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (questionId) => {
        try {
            const response = await apiClient.delete(`/admin/support-questions/${questionId}`);

            
            // التحقق من نجاح العملية
            if (response.data.status === 'success' || response.data.status === 200 || response.data.statusCode === 200) {
                showSuccessToast('تم حذف السؤال بنجاح');
                // إعادة تحميل القائمة بدون تغيير currentPage
                const updatedQuestions = questions.filter(q => q.id !== questionId);
                setQuestions(updatedQuestions);
            } else {
                throw new Error(response.data.message || 'فشل في حذف السؤال');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في حذف السؤال');
        }
    };

    const handleViewDetails = (questionId) => {
        isNavigating.current = true;
        navigate(`/admin-support/${questionId}`);
    };

    const handleEdit = (questionId) => {
        isNavigating.current = true;
        navigate(`/admin-support/edit/${questionId}`);
    };

    const handleAddNew = () => {
        isNavigating.current = true;
        navigate('/admin-support/add');
    };

    const handleSearch = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    const confirmDelete = (question) => {
        setQuestionToDelete(question);
        setShowDeleteModal(true);
    };

    const executeDelete = async () => {
        if (questionToDelete) {
            await handleDelete(questionToDelete.id);
            setShowDeleteModal(false);
            setQuestionToDelete(null);
        }
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة الدعم</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-primary"
                        onClick={handleAddNew}>
                        <FaPlus className="mx-2" />
                        إضافة سؤال جديد
                    </motion.button>
                </div>

                {/* Search and Filter */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-8">
                                <label className="form-label">البحث</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ابحث في الأسئلة..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button 
                                        className="btn text-white" 
                                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                        onClick={handleSearch}>
                                        <FaSearch />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <button 
                                    className="btn btn-outline-secondary w-100"
                                    onClick={handleClearFilters}>
                                    مسح الفلاتر
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Questions Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الأسئلة...</p>
                            </div>
                        ) : !Array.isArray(questions) || questions.length === 0 ? (
                            <div className="text-center py-5">
                                <FaQuestionCircle className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد أسئلة دعم</p>
                                <p className="text-muted small">عدد الأسئلة: {questions?.length || 0}</p>
                                <p className="text-muted small">نوع البيانات: {typeof questions}</p>
                                <p className="text-muted small">Pagination: {JSON.stringify(pagination)}</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">السؤال (عربي)</th>
                                                <th className="text-center">السؤال (إنجليزي)</th>
                                                <th className="text-center">الإجابة (عربي)</th>
                                                <th className="text-center">الإجابة (إنجليزي)</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {questions.map((question, index) => (
                                                <tr key={question.id} className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            #{index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="question-display">
                                                            <strong>{question.question_ar || 'غير محدد'}</strong>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="question-display">
                                                            <em>{question.question_en || 'غير محدد'}</em>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="answer-display">
                                                            {question.answer_ar ? (
                                                                question.answer_ar.length > 60 ? 
                                                                `${question.answer_ar.substring(0, 60)}...` : 
                                                                question.answer_ar
                                                            ) : 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="answer-display">
                                                            {question.answer_en ? (
                                                                question.answer_en.length > 60 ? 
                                                                `${question.answer_en.substring(0, 60)}...` : 
                                                                question.answer_en
                                                            ) : 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-view mx-2"
                                                                onClick={() => handleViewDetails(question.id)}
                                                                title="عرض التفاصيل">
                                                                <FaEye />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-edit mx-2"
                                                                onClick={() => handleEdit(question.id)}
                                                                title="تعديل">
                                                                <FaEdit />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-delete"
                                                                onClick={() => confirmDelete(question)}
                                                                title="حذف">
                                                                <FaTrash />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="mt-4">
                                    <EnhancedPagination
                                        currentPage={pagination?.current_page || currentPage}
                                        totalPages={pagination?.pages_count || 1}
                                        onPageChange={setCurrentPage}
                                        totalItems={pagination?.total || 0}
                                        itemsPerPage={pagination?.per_page || 10}
                                        showInfo={true}
                                        className="mt-4"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <ConfirmModal
                    isOpen={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false);
                    }}
                    onConfirm={() => {
                        executeDelete();
                    }}
                    title="تأكيد الحذف"
                    message={`هل أنت متأكد من حذف السؤال: "${questionToDelete?.question_ar || 'غير محدد'}"؟`}
                    confirmText="حذف"
                    cancelText="إلغاء"
                    confirmButtonColor="#dc3545"
                    type="danger"
                />
            </div>
        </div>
    );
};

export default AdminSupport;

// CSS للتصميم المحسن
const style = document.createElement('style');
style.textContent = `
    .table-modern {
        background: white;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border: none;
    }
    
    .table-modern thead {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }
    
    .table-modern thead th {
        border: none;
        padding: 1rem;
        font-weight: 600;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
    
    .table-modern tbody tr {
        border-bottom: 1px solid #f1f5f9;
        transition: all 0.3s ease;
    }
    
    .table-modern tbody tr:hover {
        background-color: #f8fafc;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .table-modern tbody td {
        border: none;
        padding: 1rem;
        vertical-align: middle;
    }
    
    .order-number-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.875rem;
        display: inline-block;
        min-width: 60px;
        text-align: center;
    }
    
    .question-display {
        font-weight: 500;
        color: #374151;
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 auto;
    }
    
    .answer-display {
        background: #f8fafc;
        color: #64748b;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        border: 1px solid #e2e8f0;
        max-width: 250px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 auto;
        line-height: 1.4;
    }
    
    .action-buttons {
        display: flex;
        gap: 0.5rem;
        justify-content: center;
        flex-direction: row;
        flex-wrap: nowrap;
    }
    
    .btn-view, .btn-edit, .btn-delete {
        border: none;
        color: white !important;
        padding: 0.5rem;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        width: 40px;
        height: 40px;
    }
    
    .btn-view {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3) !important;
    }
    
    .btn-edit {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3) !important;
    }
    
    .btn-delete {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
        box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3) !important;
    }
    
    .btn-view:hover, .btn-edit:hover, .btn-delete:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    
    .btn-view * {
        color: white !important;
    }
    
    .table-row-modern {
        transition: all 0.3s ease;
    }
    
    .table-row-modern:hover {
        background-color: rgba(102, 126, 234, 0.05) !important;
    }
`;
document.head.appendChild(style);
